import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  styled,
  List,
  ListItemButton,
} from "@mui/material";
import { ReactElement, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import LiquidTemplate from "@stw/liquid-template";

import MediaAttachment from "~frontend/components/MediaAttachment";
import TagChip from "~frontend/components/TagChip";
import {
  ScriptKeywords,
  ScriptTags,
} from "~frontend/components/responseScripts/styles";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import formatTagName from "~frontend/util/formatTagName";
import useAutofocusRef from "~frontend/util/useAutofocusRef";

import { ActiveCampaignContact, CampaignForTexter, Message } from "./types";

const EmptyStateContainer = styled(Box)({
  fontStyle: "italic",
});

const GroupTitle = styled(Box)({
  color: palette.darkGray,
  fontSize: "12px",
  fontWeight: 600,
  letterSpacing: "0.48px",
  textTransform: "uppercase",
});

const ScriptTitle = styled(Box)({
  color: palette.navy,
  fontSize: "14px",
  fontWeight: 700,
});

const ScriptText = styled(Box)({
  color: palette.navy,
  fontSize: "14px",
  fontWeight: 400,
});

export default function ResponseScripts({
  campaign,
  contactData,
  setMessage,
  isOptedOut,
}: {
  campaign?: CampaignForTexter;
  contactData: {
    formattedData: Record<string, string>;
    note?: ActiveCampaignContact["note"];
    firstName?: string | null;
    lastName?: string | null;
  };
  setMessage: (
    message: Message,
    tagAndGroupIds: { tagId: string; tagGroupId: string | null }[]
  ) => void;
  isOptedOut: boolean;
}): ReactElement {
  const intl = useIntl();
  const [searchText, setSearchText] = useState("");
  const { subaccount } = useSubaccount()!;

  const renderedScripts = useMemo(() => {
    function renderScript(script: string): string {
      const templateResult = LiquidTemplate.fromString(script);
      if (!templateResult.ok) {
        return intl.formatMessage({
          defaultMessage: "Invalid script",
          description: "Message shown when a script is invalid",
        });
      }

      const renderResult =
        contactData &&
        templateResult.template.render(contactData.formattedData, {
          autoAddRefcodesToABLinks: subaccount.autoAddRefcodesToABLinks,
          refcode2w: "XXXXXX",
        });
      if (!renderResult?.ok) {
        return intl.formatMessage({
          defaultMessage: "Contact data incompatible with this script",
          description:
            "Message shown when a script cannot be rendered with a given contact's data",
        });
      }

      return renderResult.result;
    }

    return (campaign?.responseScriptGroups || []).map(group => ({
      id: group.id,
      title: group.title,
      scripts: group.memberships.map(membership => ({
        id: membership.id,
        title: membership.script.title,
        renderedScript: renderScript(membership.script.script),
        tags: membership.script.tags,
        attachments: membership.script.mediaUpload
          ? [
              {
                url: membership.script.mediaUpload.publicGetUrl!,
                mimeType: membership.script.mediaUpload.contentType,
              },
            ]
          : [],
        keywords: membership.script.keywords,
      })),
    }));
  }, [
    intl,
    contactData,
    campaign?.responseScriptGroups,
    subaccount.autoAddRefcodesToABLinks,
  ]);

  let filteredResponseGroups = renderedScripts;
  const normalizedSearchText = searchText.trim().toLowerCase();
  if (normalizedSearchText) {
    filteredResponseGroups = filteredResponseGroups
      .map(group => ({
        ...group,
        scripts: group.scripts.filter(
          script =>
            group.title.toLowerCase().includes(normalizedSearchText) ||
            script.renderedScript
              .toLowerCase()
              .includes(normalizedSearchText) ||
            script.title.toLowerCase().includes(normalizedSearchText) ||
            script.tags.some(tag =>
              tag.name.toLowerCase().includes(normalizedSearchText)
            ) ||
            script.keywords.some(keyword =>
              keyword.normalizedKeyword.includes(normalizedSearchText)
            )
        ),
      }))
      .filter(group => group.scripts.length > 0);
  }

  const [searchAutofocusRef] = useAutofocusRef();

  return (
    <Box>
      <Box sx={{ marginBottom: "16px" }}>
        <TextField
          inputRef={searchAutofocusRef}
          sx={{ paddingTop: 0 }}
          fullWidth
          value={searchText}
          onChange={evt => setSearchText(evt.target.value)}
          placeholder={intl.formatMessage({
            defaultMessage: "Search for a response",
            description: "Placeholder for response script filter",
          })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {/* Disable tabbing to the button -- we auto-search on change so
                there's no need to click it, and this lets you tab directly from
                search to the response list */}
                <IconButton tabIndex={-1}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {filteredResponseGroups.length === 0 && (
        <EmptyStateContainer>
          <FormattedMessage
            defaultMessage="No responses found"
            description="Message shown when no response scripts match the filter text"
          />
        </EmptyStateContainer>
      )}
      {filteredResponseGroups.map(group => (
        <Box key={group.id} sx={{ marginBottom: "24px" }}>
          <GroupTitle>{group.title}</GroupTitle>
          <List>
            {group.scripts.map(script => (
              <ListItemButton
                key={script.id}
                disabled={isOptedOut}
                onClick={() =>
                  setMessage(
                    {
                      text: script.renderedScript,
                      attachments: script.attachments,
                    },
                    script.tags.map(tag => ({
                      tagId: tag.id,
                      tagGroupId: tag.group ? tag.group.id : null,
                    }))
                  )
                }
                sx={{
                  padding: "8px",
                  borderBottom: `1px solid ${palette.lightGray}`,
                }}
              >
                <Box>
                  <ScriptTitle>{script.title}</ScriptTitle>

                  {script.attachments.map(attachment => (
                    <MediaAttachment
                      key={attachment.url}
                      src={attachment.url}
                      contentType={attachment.mimeType}
                    />
                  ))}

                  <ScriptText>{script.renderedScript}</ScriptText>

                  {script.tags.length ? (
                    <ScriptTags>
                      {script.tags.map(tag => (
                        <TagChip key={tag.id} label={formatTagName(tag)} />
                      ))}
                    </ScriptTags>
                  ) : null}

                  <ScriptKeywords keywords={script.keywords} />
                </Box>
              </ListItemButton>
            ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}
